import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Col from 'react-bootstrap/lib/Col';

import ProductDirectoryVideoThumbnail from './ProductDirectoryVideoThumbnail';
import ProductDirectoryImage from './ProductDirectoryImage';
import Tags from '../common/dtmTags';
import {
  DataDtmActions,
  DeviceTypes,
  ImageSizes,
  Packages,
} from '../common/Constants';
import Utils from '../common/Utils';
import Classes from '../common/Classes';
import { trackImpressions, trackMixpanelEvent } from '../common/MixpanelHelper';
import mixpanelConstants from '../common/mixpanelConstants';
import FavouriteUpdateListener from '../common/components/FavouriteUpdateListener';
import FavouriteComponent from '../directory/packages/components/FavouriteComponent';
import MixpanelConstants from '../common/mixpanelConstants';

const Product = (props) => {
  const {
    translations,
    isRounded,
    getLinkURL,
    getExhibitorDetailsUrl,
    product,
    isProductInShowPlan,
    isVideoEnabled,
    isAuthorised,
    showModal,
    showPlan,
    showPlanningEnabled,
    updatedShowPlan,
  } = props;

  const productRef = useRef();
  const priorityTagTextRef = useRef();
  const [isVariantC, setIsVariantC] = useState(false);

  const mixpanelProps = {
    exhibitorId: product.exhibitingOrganisationId,
    exhibitorName: product.exhibitingOrganisationName,
    productId: product.id,
    productName: product.name,
    packageName: Utils.getPackageName(product.packageId),
    component: mixpanelConstants.component.productDirectory,
    subComponent: product.isPrioritySlotPremiumExhibitor
      ? mixpanelConstants.subComponent.priority
      : mixpanelConstants.subComponent.standard,
  };

  useEffect(() => {
    const observer = trackImpressions(
      mixpanelConstants.eventName.productImpression,
      {
        ...mixpanelProps,
        viewedElement: product.video?.id
          ? mixpanelConstants.element.productVideo
          : mixpanelConstants.element.productImage,
      },
      0
    );

    if (productRef.current) {
      observer.observe(productRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (priorityTagTextRef.current) {
      const computedStyle = window.getComputedStyle(priorityTagTextRef.current);

      if (computedStyle.getPropertyValue('display') !== 'none') {
        setIsVariantC(true);
      }
    }
  }, []);

  const linkURL = getLinkURL(product);
  const exhibitorDetailsPageUrl = getExhibitorDetailsUrl(product);
  const dataDtm = Tags.productDirectory.product;

  const productImageProps = {
    alt: translations.directory.products.productImageAltText,
    imageSize: ImageSizes.product,
    isInnovative: product.isInnovative,
    product: product,
    src: product.imageUrl,
    translations: translations.exhibitorDetails,
    isRounded,
    deviceType: DeviceTypes.tablet,
    isVideoAvailable: !!product.video?.id,
  };

  const isPremiumExhibitorProduct = product.packageId === Packages.gold || product.packageId === Packages.silver;

  const getProductCustomClasses = () => {
    if (isPremiumExhibitorProduct) {
      return `${Classes.productTile} ${Classes.enhancement} ${Classes.priorityTag} ${Classes.priorityProductContainer}`;
    }

    return `${Classes.productTile} ${Classes.enhancement}`;
  };

  const getDTMAttributes = () => {
    const dtmTag = {
      exhibitorId: product.exhibitingOrganisationId,
      productId: product.id,
      package: Utils.getPackageName(product.packageId),
    };

    if (isVariantC) {
      dtmTag.priorityTagInCTesting = isPremiumExhibitorProduct;
    }

    return JSON.stringify(dtmTag);
  };

  const imageDataDtmTag = productImageProps.isVideoAvailable ? Tags.productDirectory.vidyardVideo : Tags.productDirectory.productImage;
  const imageDataDtm = {
    tag: product.isPrioritySlotPremiumExhibitor ? imageDataDtmTag + Tags.productDirectory.prioritySlot : imageDataDtmTag,
    attributes: getDTMAttributes(),
  };
  const isInShowPlan = isProductInShowPlan(product.id, showPlan);

  const trackProductInteractedEvent = (element) => {
    trackMixpanelEvent(mixpanelConstants.eventName.productInteracted, {
      ...mixpanelProps,
      element,
    });
  };

  return (
    <Col
      xs={16}
      sm={6}
      md={4}
      className={getProductCustomClasses()}
      key={product.id}
      style={{
        '--brand-color': window.reactSettingsProductsDirectory.props.themes.brandPrimary,
        ...(isPremiumExhibitorProduct && { borderColor: window.reactSettingsProductsDirectory.props.themes.brandPrimary }),
      }}
    >
      {isPremiumExhibitorProduct && (
        <div
          className={classNames(Classes.directoryItemPremiumBannerText, Classes.primaryButtonTextColor)}
          ref={priorityTagTextRef}
          style={{ backgroundColor: window.reactSettingsProductsDirectory.props.themes.brandPrimary }}
        >
          {translations.productDirectory.premiumPackageBannerText}
        </div>
      )}

      {/* Add check for video source url here */}
      {isVideoEnabled && product.video?.id && isAuthorised ? (
        <div>
          <ProductDirectoryVideoThumbnail
            {...productImageProps}
            productId={product.id}
            onClick={showModal}
            productVideoSourceUrl='https://bitmovin-a.akamaihd.net/content/sintel/hls/playlist.m3u8'
            productVideoPosterImageUrl=''
            productVideoId={product.video?.id}
            exhibitorId={product.exhibitingOrganisationId}
          />

          <a
            href={linkURL}
            data-dtm={product.isPrioritySlotPremiumExhibitor ? dataDtm + Tags.productDirectory.prioritySlot : dataDtm}
            data-dtm-actions={DataDtmActions.view}
            data-dtm-attributes={getDTMAttributes()}
            ref={productRef}
          >
            <div onClick={() => trackProductInteractedEvent(mixpanelConstants.element.productTitle)}>
              <h4
                className={Classes.productName}
                data-dtm={product.isPrioritySlotPremiumExhibitor
                    ? Tags.productDirectory.productName + Tags.productDirectory.prioritySlot
                    : Tags.productDirectory.productName
                }
                data-dtm-actions={DataDtmActions.click}
                data-dtm-attributes={getDTMAttributes()}
              >
                {product.name}
              </h4>
            </div>
          </a>

          <a href={exhibitorDetailsPageUrl}>
            <div
              className={Classes.productExhibitorName}
              data-dtm={product.isPrioritySlotPremiumExhibitor
                  ? Tags.productDirectory.exhibitorName + Tags.productDirectory.prioritySlot
                  : Tags.productDirectory.exhibitorName
              }
              data-dtm-actions={DataDtmActions.click}
              data-dtm-attributes={getDTMAttributes()}
            >
              {product.exhibitingOrganisationName}
            </div>
          </a>
        </div>
      ) : (
        <div>
          <a
            href={linkURL}
            data-dtm={product.isPrioritySlotPremiumExhibitor
                ? dataDtm + Tags.productDirectory.prioritySlot
                : dataDtm
            }
            data-dtm-actions={DataDtmActions.view}
            data-dtm-attributes={getDTMAttributes()}
            ref={productRef}
          >
            <div>
              <ProductDirectoryImage
                {...productImageProps}
                imageDataDtm={imageDataDtm}
                trackProductInteractedEvent={trackProductInteractedEvent}
                product={product}
              />

              <h4
                className={Classes.productName}
                data-dtm={product.isPrioritySlotPremiumExhibitor
                    ? Tags.productDirectory.productName + Tags.productDirectory.prioritySlot
                    : Tags.productDirectory.productName
                }
                data-dtm-actions={DataDtmActions.click}
                data-dtm-attributes={getDTMAttributes()}
                onClick={() => trackProductInteractedEvent(mixpanelConstants.element.productTitle)}
              >
                {product.name}
              </h4>
            </div>
          </a>

          <a href={exhibitorDetailsPageUrl}>
            <div
              className={Classes.productExhibitorName}
              data-dtm={product.isPrioritySlotPremiumExhibitor
                  ? Tags.productDirectory.exhibitorName + Tags.productDirectory.prioritySlot
                  : Tags.productDirectory.exhibitorName
              }
              data-dtm-actions={DataDtmActions.click}
              data-dtm-attributes={getDTMAttributes()}
              onClick={() =>
                trackMixpanelEvent(mixpanelConstants.eventName.exhibitorClicked,
                  {
                    exhibitorId: product.exhibitingOrganisationId,
                    exhibitorName: product.exhibitingOrganisationName,
                    packageName: Utils.getPackageName(product.packageId),
                    element: mixpanelConstants.element.exhibitorTitle,
                    component: mixpanelConstants.component.productDirectory,
                    productId: product.id,
                    productName: product.name,
                  }
                )
              }
            >
              {product.exhibitingOrganisationName}
            </div>
          </a>
        </div>
      )}

      <div className={Classes.favouriteContainer}>
        <FavouriteUpdateListener
          showPlan={showPlan}
          showPlanningEnabled={showPlanningEnabled}
          favouriteCallback={updatedShowPlan}
          unfavouriteCallback={updatedShowPlan}
        />

        <FavouriteComponent
          product={product}
          translations={translations.productDirectory.favourite}
          initialState={isInShowPlan}
          showPlanningEnabled={showPlanningEnabled}
          className={Classes.showPlanContainer}
          type='product'
          dtmData={Tags.productDirectory}
          component={MixpanelConstants.component.productDirectory}
          isPrioritySlotPremiumExhibitor={product.isPrioritySlotPremiumExhibitor}
          isVariantC={isVariantC}
        />
      </div>
    </Col>
  );
};

Product.propTypes = {
  translations: PropTypes.object.isRequired,
  isRounded: PropTypes.bool,
  showPlanningEnabled: PropTypes.bool,
  getLinkURL: PropTypes.func,
  getExhibitorDetailsUrl: PropTypes.func,
  product: PropTypes.object,
  isProductInShowPlan: PropTypes.func,
  isVideoEnabled: PropTypes.bool,
  isAuthorised: PropTypes.bool,
  showModal: PropTypes.func,
  showPlan: PropTypes.object,
  updatedShowPlan: PropTypes.func,
};

export default Product;
